import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { useQueryParams } from "@hooks/useQueryParams";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { fetchUploadImagesByUrl } from "@apis/http/api";
import type { ProductImage } from "./videoCreatingSlice";
import { selectVideoParams, setDescParams } from "./videoCreatingSlice";
import { useVideoModelRules } from "./useVideoModelRules";
import { getPreviewVideoCredit } from "@apis/http/api";

/**
 * 图片 URL 转换
 */
export async function uploadAndReplaceImages(images: ProductImage[]): Promise<ProductImage[]> {
  const needUploadImages = images.filter((img) => !img.isChangedUrl);
  if (needUploadImages.length === 0) return images;

  const needUploadUrls = needUploadImages.map((img) => img.url);
  const results: any = await fetchUploadImagesByUrl(needUploadUrls.map((url) => ({ url, description: "" })) as any);

  // 构建原始 url -> 上传结果的映射
  const resultMap = new Map<string, (typeof results)[number]>();
  needUploadUrls.forEach((url, idx) => {
    if (results[idx]) {
      resultMap.set(url, results[idx]);
    }
  });

  return images
    .map((img) => {
      const result = resultMap.get(img.url);
      if (!result) return img;
      // 上传失败，标记为 null 后续过滤
      if (!result.success) return null;
      return {
        ...img,
        id: result.assetId,
        url: result.imageUrl,
        isChangedUrl: true,
      };
    })
    .filter((img): img is ProductImage => img !== null);
}

export const useVideoCreatingReady = (updateReady: Function, resetToInit?: Function) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [productId, country] = useQueryParams(["productId", "country"]);

  useEffect(() => {
    dispatch(
      updateReady({
        productId,
        country,
      }),
    );
    return () => {
      if (resetToInit) {
        dispatch(resetToInit());
      }
    };
  }, [location]);
};

/**
 * 调用接口获取视频创建积分消耗，参数变化时自动重新请求
 * totalCost 持久化在 Redux 中，组件重新挂载时若参数未变则跳过请求
 * @param enabled 是否启用请求，默认 true；传 false 时跳过接口调用
 */
export const useConsumeCredit = (enabled = true) => {
  const dispatch = useAppDispatch();
  const { aspectRatio, duration, quantity, resolution, internalModelId, totalCost, creditCacheKey } =
    useAppSelector(selectVideoParams);
  const { isModelAllowed } = useVideoModelRules();
  const cacheKey = `${aspectRatio}_${duration}_${quantity}_${resolution}_${internalModelId}`;
  const prevKeyRef = useRef(creditCacheKey);

  useEffect(() => {
    if (!enabled) return;
    if (!aspectRatio || !duration || !quantity || !resolution || !internalModelId) return;
    if (!isModelAllowed(duration, internalModelId)) return;
    if (cacheKey === prevKeyRef.current) return;
    prevKeyRef.current = cacheKey;

    let cancelled = false;
    (async () => {
      try {
        const res: any = await getPreviewVideoCredit({
          aspectRatio,
          duration,
          quantity,
          resolution,
          internalModelId,
        } as unknown as any);
        if (!cancelled) {
          dispatch(setDescParams({ totalCost: res || 0, creditCacheKey: cacheKey }));
        }
      } catch (error) {
        console.error("Failed to fetch consume credit:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cacheKey, enabled, isModelAllowed]);

  return totalCost;
};
